// src/pages/Cars/AllCars.jsx
import { DeleteIcon, EditIcon, ViewIcon } from "@chakra-ui/icons";
import {
    Badge,
    Box,
    Button,
    Center,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AccessDenied from "../../components/AccessDenied";
import Loading from "../../components/Loading/Loading";
import Pagination from "../../components/Pagination";
import PopoverId from "../../components/PopoverId";
import TitleHeader from "../../components/TitleHeader";
import { hasPermission, useTableColors } from "../../Utils";

import {
    assignCar,
    createCar,
    deleteCar,
    fetchAllCouriers,
    getCars,
    unassignCar,
    updateCar
} from "../../api";

export default function AllCars() {
    const navigate = useNavigate();
    const toast = useToast();

    const {
        rowBgEven,
        rowBgOdd,
        rowHoverBg,
        borderColor,
        subTextColor,
        theadBg,
    } = useTableColors();

    const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
    const { isOpen: isAssignOpen, onOpen: onOpenAssign, onClose: onCloseAssign } = useDisclosure();
    const { isOpen: isUnassignOpen, onOpen: onOpenUnassign, onClose: onCloseUnassign } = useDisclosure();

    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingProcess, setLoadingProcess] = useState(false);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [deleted, setDeleted] = useState("false");

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 100;

    const [selectedCar, setSelectedCar] = useState(null);

    const [newCar, setNewCar] = useState({
        make: "",
        model: "",
        year: "",
        plate: "",
        vin: "",
        status: "active",
    });

    // assign modal
    const [couriers, setCouriers] = useState([]);
    const [selectedCourierId, setSelectedCourierId] = useState("");

    const canCreate = hasPermission("createCars");
    const canUpdate = hasPermission("updateCars");
    const canDelete = hasPermission("deleteCars");
    const canAssign = hasPermission("assignCars");
    const canView = hasPermission("viewCars");

    const toastCfg = useMemo(
        () => ({
            position: "top-right",
            isClosable: true,
            duration: 3500, // auto-dismiss
        }),
        []
    );

    const showSuccess = (title, description) => {
        toast({
            ...toastCfg,
            status: "success",
            title: title || "Success",
            description: description || "",
        });
    };

    const showError = (title, description) => {
        toast({
            ...toastCfg,
            status: "error",
            title: title || "Error",
            description: description || "Something went wrong",
        });
    };

    // Friendly error mapping (backend may return { success:false, code, message })
    const friendlyError = (res, fallbackTitle = "Request Failed") => {
        const code = res?.code || res?.error?.code || null;
        const msg = res?.message || res?.error?.message || "Request failed";

        const map = {
            OPEN_CYCLE_EXISTS:
                "This car has an open cycle for the current courier. The courier must complete the dropoff inspection before you can assign/unassign.",
        };

        const friendly = code && map[code] ? map[code] : msg;

        // Separate “title” from details for clarity
        return { title: fallbackTitle, message: friendly };
    };

    const validateCar = (c) => {
        if (!c.make || !c.model || !c.year || !c.plate) return "Please fill in all required fields.";
        const y = Number(c.year);
        if (!Number.isFinite(y) || y < 1900 || y > new Date().getFullYear() + 1) return "Year is invalid.";
        return null;
    };

    const loadCars = async (page = 1) => {
        setLoading(true);
        setError(null);

        try {
            const res = await getCars({
                page,
                limit: itemsPerPage,
                search,
                status,
                deleted,
            });

            if (res?.success) {
                setCars(res.data?.cars || []);
                setCurrentPage(Number(res.data?.currentPage || page));
                setTotalPages(Number(res.data?.totalPages || 1));
            } else {
                setError(res?.message || "Failed to load cars");
            }
        } catch {
            setError("Failed to load cars");
        }

        setLoading(false);
    };

    useEffect(() => {
        loadCars(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => loadCars(1);

    const statusBadge = (s) => {
        if (s === "maintenance") return <Badge colorScheme="orange">maintenance</Badge>;
        if (s === "retired") return <Badge colorScheme="red">retired</Badge>;
        return <Badge colorScheme="green">active</Badge>;
    };

    const formatCourierName = (courier) => {
        if (!courier) return "-";
        const n = courier.name || `${courier.firstName || ""} ${courier.lastName || ""}`.trim();
        return n || "-";
    };

    const closeAllModals = () => {
        setLoadingProcess(false);
        onCloseCreate();
        onCloseEdit();
        onCloseDelete();
        onCloseAssign();
        onCloseUnassign();
    };

    /* ===================== CREATE ===================== */

    const handleOpenCreate = () => {
        setNewCar({ make: "", model: "", year: "", plate: "", vin: "", status: "active" });
        onOpenCreate();
    };

    const handleCreate = async () => {
        const msg = validateCar(newCar);
        if (msg) return showError("Validation", msg);

        setLoadingProcess(true);
        try {
            const res = await createCar({ ...newCar, year: Number(newCar.year) });

            if (res?.success) {
                showSuccess("Car Created", `Car ${res?.data?.plate || newCar.plate} created successfully.`);
                onCloseCreate();
                await loadCars(1);
            } else {
                const f = friendlyError(res, "Create Failed");
                showError(f.title, f.message);
            }
        } catch {
            showError("Create Failed", "Server error while creating car.");
        }
        setLoadingProcess(false);
    };

    /* ===================== EDIT ===================== */

    const handleOpenEdit = (car) => {
        setSelectedCar({
            ...car,
            year: (car?.year ?? "").toString(),
        });
        onOpenEdit();
    };

    const handleEdit = async () => {
        const msg = validateCar(selectedCar);
        if (msg) return showError("Validation", msg);

        setLoadingProcess(true);
        try {
            const res = await updateCar(selectedCar._id, {
                make: selectedCar.make,
                model: selectedCar.model,
                year: Number(selectedCar.year),
                plate: selectedCar.plate,
                vin: selectedCar.vin,
                status: selectedCar.status,
            });

            if (res?.success) {
                showSuccess("Car Updated", `Car ${selectedCar.plate} updated successfully.`);
                onCloseEdit();
                await loadCars(currentPage);
            } else {
                const f = friendlyError(res, "Update Failed");
                showError(f.title, f.message);
            }
        } catch {
            showError("Update Failed", "Server error while updating car.");
        }
        setLoadingProcess(false);
    };

    /* ===================== DELETE ===================== */

    const handleOpenDelete = (car) => {
        setSelectedCar(car);
        onOpenDelete();
    };

    const handleDelete = async () => {
        setLoadingProcess(true);
        try {
            const res = await deleteCar(selectedCar._id);

            if (res?.success) {
                showSuccess("Car Deleted", `Car ${selectedCar.plate} deleted successfully.`);
                onCloseDelete();
                await loadCars(currentPage);
            } else {
                const f = friendlyError(res, "Delete Failed");
                showError(f.title, f.message);
            }
        } catch {
            showError("Delete Failed", "Server error while deleting car.");
        }
        setLoadingProcess(false);
    };

    /* ===================== ASSIGN ===================== */

    const loadCouriers = async () => {
        try {
            const res = await fetchAllCouriers(1, 1000);
            if (res?.success) setCouriers(res.data?.couriers || []);
            else setCouriers([]);
        } catch {
            setCouriers([]);
        }
    };

    const handleOpenAssign = async (car) => {
        setSelectedCar(car);
        setSelectedCourierId("");
        await loadCouriers();
        onOpenAssign();
    };

    const handleAssign = async () => {
        if (!selectedCar?._id) return showError("Assign Failed", "Car not selected.");
        if (!selectedCourierId) return showError("Validation", "Please select a courier.");

        setLoadingProcess(true);
        try {
            const res = await assignCar(selectedCar._id, selectedCourierId);

            if (res?.success) {
                showSuccess("Assigned", `Car ${selectedCar.plate} assigned successfully.`);
                onCloseAssign();
                await loadCars(currentPage);
            } else {
                const f = friendlyError(res, "Assign Failed");
                showError(f.title, f.message);
            }
        } catch {
            showError("Assign Failed", "Server error while assigning car.");
        }
        setLoadingProcess(false);
    };

    /* ===================== UNASSIGN ===================== */

    const handleOpenUnassign = (car) => {
        setSelectedCar(car);
        onOpenUnassign();
    };

    const handleUnassign = async () => {
        if (!selectedCar?._id) return showError("Unassign Failed", "Car not selected.");

        setLoadingProcess(true);
        try {
            const res = await unassignCar(selectedCar._id);

            if (res?.success) {
                showSuccess("Unassigned", `Car ${selectedCar.plate} unassigned successfully.`);
                onCloseUnassign();
                await loadCars(currentPage);
            } else {
                const f = friendlyError(res, "Unassign Failed");
                showError(f.title, f.message);
            }
        } catch {
            showError("Unassign Failed", "Server error while unassigning car.");
        }
        setLoadingProcess(false);
    };

    /* ===================== GUARDS ===================== */

    if (loading) {
        return (
            <Center mt={"20%"}>
                <Loading />
            </Center>
        );
    }

    if (!canView) return <AccessDenied msg="Access denied" />;
    if (error) return <AccessDenied msg={error} />;

    /* ===================== UI ===================== */

    return (
        <Box maxW="full" mx="auto">
            <Box>
                <TitleHeader
                    title="Cars"
                    buttonText={canCreate ? "Create New Car" : null}
                    onClick={canCreate ? handleOpenCreate : null}
                />

                {/* Filters */}
                <Box border="1px solid" borderColor={borderColor} borderRadius="md" p={4} mb={4}>
                    <HStack spacing={4} flexWrap="wrap">
                        <FormControl maxW="360px">
                            <FormLabel>Search</FormLabel>
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Make, model, plate, VIN..."
                            />
                        </FormControl>

                        <FormControl maxW="220px">
                            <FormLabel>Status</FormLabel>
                            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="">All</option>
                                <option value="active">Active</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="retired">Retired</option>
                            </Select>
                        </FormControl>

                        <FormControl maxW="220px">
                            <FormLabel>Deleted</FormLabel>
                            <Select value={deleted} onChange={(e) => setDeleted(e.target.value)}>
                                <option value="false">Active only</option>
                                <option value="true">Deleted only</option>
                            </Select>
                        </FormControl>

                        <Button mt={8} onClick={handleSearch} colorScheme="blue">
                            Apply
                        </Button>
                    </HStack>
                </Box>

                {cars.length > 0 ? (
                    <>
                        <Table size="sm">
                            <Thead bg={theadBg}>
                                <Tr>
                                    <Th>#</Th>
                                    <Th>Car</Th>
                                    <Th>Plate</Th>
                                    <Th>Status</Th>
                                    <Th>Current Odometer</Th>
                                    <Th>Assigned To</Th>
                                    <Th textAlign="right">Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {cars.map((car, index) => {
                                    const assignedCourier = car.activeAssignment?.courierId || null;
                                    const assignedName = formatCourierName(assignedCourier);
                                    const odo =
                                        typeof car.currentOdometer === "number"
                                            ? car.currentOdometer.toLocaleString()
                                            : "-";

                                    return (
                                        <Tr
                                            key={car._id}
                                            borderBottom="2px solid"
                                            borderColor={borderColor}
                                            _hover={{ bg: rowHoverBg, color: "white" }}
                                            bg={index % 2 === 0 ? rowBgEven : rowBgOdd}
                                        >
                                            <Td>
                                                <PopoverId place="Cars" index={index} content={car._id} />
                                            </Td>

                                            <Td cursor="default">
                                                <Text fontWeight="bold">
                                                    {car.year} {car.make} {car.model}
                                                </Text>
                                                <Text fontSize="xs" color={subTextColor}>
                                                    VIN: {car.vin || "-"}
                                                </Text>
                                            </Td>

                                            <Td>
                                                <Badge colorScheme="purple">{car.plate}</Badge>
                                            </Td>

                                            <Td>{statusBadge(car.status)}</Td>

                                            <Td>
                                                <Text fontWeight="bold">{odo}</Text>
                                            </Td>

                                            <Td>
                                                {assignedCourier ? (
                                                    <Text
                                                        fontWeight="bold"
                                                        cursor="pointer"
                                                        onClick={() =>
                                                            navigate(`/couriers/details/${assignedCourier._id}`)
                                                        }
                                                    >
                                                        {assignedName}
                                                    </Text>
                                                ) : (
                                                    <Text color={subTextColor}>-</Text>
                                                )}
                                            </Td>

                                            <Td textAlign="right">
                                                <HStack justifyContent="flex-end" spacing={2}>
                                                    <Tooltip label="View Car Details" placement="top">
                                                        <Button
                                                            size="xs"
                                                            colorScheme="blue"
                                                            onClick={() => navigate(`/car/details/${car._id}`)}
                                                        >
                                                            <ViewIcon />
                                                        </Button>
                                                    </Tooltip>

                                                    {canAssign && (
                                                        assignedCourier ? (
                                                            <Tooltip label="Unassign Courier" placement="top">
                                                                <Button
                                                                    size="xs"
                                                                    colorScheme="orange"
                                                                    onClick={() => handleOpenUnassign(car)}
                                                                >
                                                                    Unassign
                                                                </Button>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip label="Assign Courier" placement="top">
                                                                <Button
                                                                    size="xs"
                                                                    colorScheme="purple"
                                                                    onClick={() => handleOpenAssign(car)}
                                                                >
                                                                    Assign
                                                                </Button>
                                                            </Tooltip>
                                                        )
                                                    )}

                                                    {canUpdate && !car.deletedAt && (
                                                        <Tooltip label="Update Car" placement="top">
                                                            <Button
                                                                size="xs"
                                                                colorScheme="green"
                                                                onClick={() => handleOpenEdit(car)}
                                                            >
                                                                <EditIcon />
                                                            </Button>
                                                        </Tooltip>
                                                    )}

                                                    {canDelete && !car.deletedAt && (
                                                        <Tooltip label="Delete Car" placement="top">
                                                            <Button
                                                                size="xs"
                                                                colorScheme="red"
                                                                onClick={() => handleOpenDelete(car)}
                                                            >
                                                                <DeleteIcon />
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            handlePageChange={(p) => loadCars(p)}
                        />
                    </>
                ) : (
                    <AccessDenied msg="No cars found!" />
                )}

                {/* ===================== CREATE MODAL ===================== */}
                <Modal isOpen={isCreateOpen} size="3xl" onClose={closeAllModals}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Create New Car</ModalHeader>

                        <ModalBody>
                            <HStack spacing={4}>
                                <FormControl>
                                    <FormLabel>Make</FormLabel>
                                    <Input
                                        value={newCar.make}
                                        onChange={(e) => setNewCar({ ...newCar, make: e.target.value })}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Model</FormLabel>
                                    <Input
                                        value={newCar.model}
                                        onChange={(e) => setNewCar({ ...newCar, model: e.target.value })}
                                    />
                                </FormControl>
                            </HStack>

                            <HStack spacing={4} mt={4}>
                                <FormControl>
                                    <FormLabel>Year</FormLabel>
                                    <Input
                                        value={newCar.year}
                                        onChange={(e) => setNewCar({ ...newCar, year: e.target.value })}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Plate</FormLabel>
                                    <Input
                                        value={newCar.plate}
                                        onChange={(e) => setNewCar({ ...newCar, plate: e.target.value })}
                                    />
                                </FormControl>
                            </HStack>

                            <HStack spacing={4} mt={4}>
                                <FormControl>
                                    <FormLabel>VIN</FormLabel>
                                    <Input
                                        value={newCar.vin}
                                        onChange={(e) => setNewCar({ ...newCar, vin: e.target.value })}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                        value={newCar.status}
                                        onChange={(e) => setNewCar({ ...newCar, status: e.target.value })}
                                    >
                                        <option value="active">active</option>
                                        <option value="maintenance">maintenance</option>
                                        <option value="retired">retired</option>
                                    </Select>
                                </FormControl>
                            </HStack>
                        </ModalBody>

                        <ModalFooter>
                            <Button onClick={closeAllModals} mr={3}>
                                Cancel
                            </Button>
                            <Button
                                isLoading={loadingProcess}
                                colorScheme="blue"
                                onClick={handleCreate}
                            >
                                Create
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* ===================== EDIT MODAL ===================== */}
                {selectedCar && (
                    <Modal isOpen={isEditOpen} size="3xl" onClose={closeAllModals}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Edit Car</ModalHeader>

                            <ModalBody>
                                <HStack spacing={4}>
                                    <FormControl>
                                        <FormLabel>Make</FormLabel>
                                        <Input
                                            value={selectedCar.make || ""}
                                            onChange={(e) => setSelectedCar({ ...selectedCar, make: e.target.value })}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Model</FormLabel>
                                        <Input
                                            value={selectedCar.model || ""}
                                            onChange={(e) => setSelectedCar({ ...selectedCar, model: e.target.value })}
                                        />
                                    </FormControl>
                                </HStack>

                                <HStack spacing={4} mt={4}>
                                    <FormControl>
                                        <FormLabel>Year</FormLabel>
                                        <Input
                                            value={selectedCar.year || ""}
                                            onChange={(e) => setSelectedCar({ ...selectedCar, year: e.target.value })}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Plate</FormLabel>
                                        <Input
                                            value={selectedCar.plate || ""}
                                            onChange={(e) => setSelectedCar({ ...selectedCar, plate: e.target.value })}
                                        />
                                    </FormControl>
                                </HStack>

                                <HStack spacing={4} mt={4}>
                                    <FormControl>
                                        <FormLabel>VIN</FormLabel>
                                        <Input
                                            value={selectedCar.vin || ""}
                                            onChange={(e) => setSelectedCar({ ...selectedCar, vin: e.target.value })}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Status</FormLabel>
                                        <Select
                                            value={selectedCar.status || "active"}
                                            onChange={(e) => setSelectedCar({ ...selectedCar, status: e.target.value })}
                                        >
                                            <option value="active">active</option>
                                            <option value="maintenance">maintenance</option>
                                            <option value="retired">retired</option>
                                        </Select>
                                    </FormControl>
                                </HStack>
                            </ModalBody>

                            <ModalFooter>
                                <Button onClick={closeAllModals} mr={3}>
                                    Cancel
                                </Button>
                                <Button
                                    isLoading={loadingProcess}
                                    colorScheme="blue"
                                    onClick={handleEdit}
                                >
                                    Save
                                </Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                )}

                {/* ===================== DELETE MODAL ===================== */}
                <Modal isOpen={isDeleteOpen} onClose={closeAllModals}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>

                        <ModalBody>
                            Are you sure you want to delete car <strong>{selectedCar?.plate}</strong>?
                        </ModalBody>

                        <ModalFooter>
                            <Button
                                isLoading={loadingProcess}
                                colorScheme="red"
                                mr={3}
                                onClick={handleDelete}
                            >
                                Delete
                            </Button>
                            <Button onClick={closeAllModals}>Cancel</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* ===================== ASSIGN MODAL ===================== */}
                <Modal isOpen={isAssignOpen} size="lg" onClose={closeAllModals}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Assign Car</ModalHeader>

                        <ModalBody>
                            <FormControl>
                                <FormLabel>Select courier</FormLabel>
                                <Select
                                    value={selectedCourierId}
                                    onChange={(e) => setSelectedCourierId(e.target.value)}
                                >
                                    <option value="">Select...</option>
                                    {couriers.map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim()}{" "}
                                            {c.email ? `(${c.email})` : ""}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </ModalBody>

                        <ModalFooter>
                            <Button onClick={closeAllModals} mr={3}>
                                Cancel
                            </Button>
                            <Button
                                isLoading={loadingProcess}
                                colorScheme="purple"
                                onClick={handleAssign}
                            >
                                Assign
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* ===================== UNASSIGN MODAL ===================== */}
                <Modal isOpen={isUnassignOpen} onClose={closeAllModals}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Unassign Car</ModalHeader>

                        <ModalBody>
                            Unassign car <strong>{selectedCar?.plate}</strong> from the current courier?
                        </ModalBody>

                        <ModalFooter>
                            <Button
                                isLoading={loadingProcess}
                                colorScheme="orange"
                                mr={3}
                                onClick={handleUnassign}
                            >
                                Unassign
                            </Button>
                            <Button onClick={closeAllModals}>Cancel</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </Box>
        </Box>
    );
}
